// Login.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaSignInAlt, FaHome } from "react-icons/fa";
import { MdEmail as MailIcon, MdLock as LockIcon } from "react-icons/md";
import { toast } from 'react-toastify';
import {
  SET_LOGIN, SET_NAME, SET_USER, SET_USER_TYPE
} from '../../redux/features/auth/authSlice';
import { validateEmail, loginUser } from '../../services/authService';
import ButtonLoading from '../../loader/ButtonLoader';
import logo from '../../assets/megertuhome.png';
import { MdCalculate } from "react-icons/md";

const initialState = { email: "", password: "" };

export const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [userForm,  setUserForm]  = useState(initialState);
  const [showPass,  setShowPass]  = useState(false);

  const { email, password } = userForm;

  const handleChange = (e) => {
    setUserForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const login = async (e) => {
    e.preventDefault();
    if (!email || !password)   return toast.error("Please fill in all fields");
    if (!validateEmail(email)) return toast.error("Invalid email address");
    setIsLoading(true);
    try {
      const data = await loginUser({ email, password });
      dispatch(SET_LOGIN(true));
      dispatch(SET_NAME(data.firstname));
      dispatch(SET_USER(data));
      dispatch(SET_USER_TYPE(data.userType));
      navigate("/dashboard");
    } catch {}
    setIsLoading(false);
  };

  return (
    <div className="min-vh-100 d-flex">

      {/* ── Left panel ─────────────────────────────────────── */}
      <div
        className="d-none d-lg-flex flex-column align-items-center
                   justify-content-center p-5 text-center text-white"
        style={{
          width: "42%",
          background: "linear-gradient(160deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          position: "relative",
          overflow: "hidden",
        }}>

        {/* Decorative circles */}
        <div style={{
          position: "absolute", top: -80, right: -80,
          width: 320, height: 320, borderRadius: "50%",
          background: "rgba(255,255,255,0.04)", pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: -60, left: -60,
          width: 240, height: 240, borderRadius: "50%",
          background: "rgba(255,255,255,0.04)", pointerEvents: "none"
        }} />

        {/* Logo */}
        <div className="rounded-4 p-3 mb-4"
          style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(4px)" }}>
          <img src={logo} alt="Megertu Ceramics"
            style={{ width: 160, height: "auto", objectFit: "contain",
                     filter: "brightness(0) invert(1)" }} />
        </div>

        <h2 className="fw-bold fs-1 mb-1"> <span className='text-warning'>Megertu</span> Ceramics</h2>
        <p className="fs-3 fw-bold mb-5 text-success" >Inventory System</p>

        {/* Quick guide card */}
        <div className="rounded-4 p-4 text-start w-100"
          style={{
            maxWidth: 340,
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(6px)",
          }}>
          <p className="fw-semibold fs-4 mb-3 text-uppercase"
            style={{ letterSpacing: "0.12em", opacity: 0.55 }}>
            Quick Guide
          </p>
          {[
            "Enter your email and password to sign in.",
            "Use your admin credentials to access admin features.",
            "Use employee credentials to access employee features.",
            "First time? Change your password from Profile after logging in.",
            "No account? Ask your administrator to create one.",
          ].map((tip, i) => (
            <div key={i} className="d-flex gap-3 mb-3 align-items-start ">
              <div className="rounded-circle d-flex align-items-center
                              justify-content-center flex-shrink-0 fw-bold fs-4 text-success-emphasis"
                style={{
                  width: 26, height: 26, minWidth: 26,
                  background: "rgba(255,255,255,0.15)",
                  fontSize: "0.8rem",
                }}>
                {i + 1}
              </div>
              <p className="mb-0 fs-4 text-light" style={{ lineHeight: 1.5 }}>{tip}</p>
            </div>
          ))}
        </div>

        <p className="mt-5 mb-0 fs-5" style={{ opacity: 0.35 }}>
          &copy; {new Date().getFullYear()} Yebirihan Technology
        </p>
      </div>

      {/* ── Right panel — form ─────────────────────────────── */}
      <div className="flex-grow-1 d-flex flex-column bg-light">

        {/* Top bar */}
        <div className="bg-white border-bottom px-4 py-3
                        d-flex align-items-center justify-content-between shadow-sm">
          {/* Mobile: show logo + name */}
          <div className="d-flex d-lg-none align-items-center gap-2">
            <img src={logo} alt="logo"
              style={{ height: 36, width: "auto", objectFit: "contain" }} />
            <span className="fw-bold fs-4">Megertu Ceramics</span>
          </div>
          <div className="d-none d-lg-block" />
          <div className="col-md-6 text-end justify-content-center">
            <a href="https://ceramics-calculator.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary me-3 mt-0 mb-1 fs-3">
              <MdCalculate size={18} className="mb-1"/>
              Calculator
            </a>
            <Link to="/"
              className="btn btn-primary me-3 mt-0 mb-1 fs-3">
              <FaHome size={18} /> Home
            </Link>
          </div>
        </div>

        {/* Form centered */}
        <div className="flex-grow-1 d-flex align-items-center
                        justify-content-center px-3 py-5">
          <div className="w-100" style={{ maxWidth: 420 }}>

            {/* Heading */}
            <div className="text-center mb-4">
              <div className="bg-primary bg-opacity-10 rounded-circle
                              d-flex align-items-center justify-content-center
                              mx-auto mb-3"
                style={{ width: 60, height: 60 }}>
                <FaSignInAlt size={24} className="text-primary" />
              </div>
              <h2 className="fw-bold fs-2 mb-1">Sign In</h2>
              <p className="text-muted fs-4 mb-0">
                Enter your credentials to continue
              </p>
            </div>

            {/* Form card */}
            <div className="card border-0 rounded-4 shadow-sm">
              <div className="card-body p-4">
                <form className='border-0' onSubmit={login} noValidate>
                  <div className="d-flex flex-column gap-3">

                    {/* Email */}
                    <div>
                      <label className="form-label fw-semibold fs-4" htmlFor="email">
                        Email Address
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light border-end-0">
                          <MailIcon size={20} className="text-muted" />
                        </span>
                        <input
                          id="email" type="email"
                          className="form-control border-start-0 fs-4"
                          placeholder="your@email.com"
                          name="email" value={email}
                          onChange={handleChange}
                          autoComplete="email" />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="form-label fw-semibold fs-4" htmlFor="password">
                        Password
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light border-end-0">
                          <LockIcon size={20} className="text-muted" />
                        </span>
                        <input
                          id="password"
                          type={showPass ? "text" : "password"}
                          className="form-control border-start-0 border-end-0 fs-4"
                          placeholder="Enter your password"
                          name="password" value={password}
                          onChange={handleChange}
                          autoComplete="current-password" />
                        <button type="button"
                          className="input-group-text bg-light border-start-0
                                     btn btn-link text-muted text-decoration-none
                                     fs-4 px-3"
                          onClick={() => setShowPass(p => !p)}
                          tabIndex={-1}>
                          {showPass ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>

                    {/* Submit */}
                    <div>
                      {isLoading ? (
                        <ButtonLoading
                          className="btn btn-primary w-100 rounded-3 fw-semibold py-2 fs-4"
                          disabled>
                          Signing in...
                        </ButtonLoading>
                      ) : (
                        <button type="submit"
                          className="btn btn-primary w-100 rounded-3 fw-semibold py-2 fs-4
                                     d-flex align-items-center justify-content-center gap-2" style={{marginLeft: "3px"}}>
                          <FaSignInAlt size={16} />
                          Sign In
                        </button>
                      )}
                    </div>

                    <hr className="my-0 opacity-25" />

                    {/* Forgot password */}
                    <div className="text-center">
                      <Link to="/ForgotPassword"
                        className="text-primary fw-medium fs-4 text-decoration-none">
                        Forgot your password?
                      </Link>
                    </div>

                  </div>
                </form>
              </div>
            </div>

            {/* Helper notes */}
            <div className="mt-3 px-1">
              <p className="text-muted fs-4 text-center mb-2">
                Don't have an account?{" "}
                <span className="text-primary fw-semibold">
                  Ask your administrator
                </span>
              </p>
              <div className="alert alert-info border-0 rounded-3 py-2 px-3 mb-0 fs-4">
                <strong>First time logging in?</strong> After signing in, go to{" "}
                <strong>Profile</strong> and change your password.
              </div>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <div className="bg-white border-top px-4 py-3 text-center">
          <p className="text-muted fs-5 mb-0">
            &copy; {new Date().getFullYear()} Megertu Ceramics —
            Developed by Yebirihan Technology
          </p>
        </div>

      </div>
    </div>
  );
};